import React from "react";
import StudentListBase from "../../shared/students/StudentListBase";
import { API_BASE_URL } from "../../../utils/api";

// Student List Component - Connects class-specific student management directly with the backend
function StudentList({ campusId, classId, onBack, showToast, onLogout }) {
  
  const [className, setClassName] = React.useState(`Class #${classId}`);

  React.useEffect(() => {
    const fetchClassInfo = async () => {
      const token = localStorage.getItem("als_token");
      try {
        const res = await fetch(
          `${API_BASE_URL}/api/classes?campus_id=${campusId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.ok) {
          const classes = await res.json();
          const cls = classes.find((c) => String(c.id) === String(classId));
          if (cls) {
            setClassName(`${cls.name}${cls.section ? ` — ${cls.section}` : ""}`);
          }
        }
      } catch (err) {
        console.error("Failed to load class info from backend", err);
      }
    };
    if (campusId && classId) {
      fetchClassInfo();
    }
  }, [campusId, classId]);

  // Save/Update student details
  const handleSaveEdit = async (student) => {
    const token = localStorage.getItem("als_token");
    const apiStudentId = student.student_id || student.id;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/students/${apiStudentId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: student.name,
            father_name: student.father_name || null,
            gender: student.gender,
            date_of_birth: student.date_of_birth || null,
            campus_id: campusId ? parseInt(campusId, 10) : null,
            class_id: classId ? parseInt(classId, 10) : null,
            class_name: student.class_name || null,
            class_section: student.class_section || null,
            admission_date: student.admission_date,
            student_status: student.student_status || "Active",
            actual_fee_amount: student.fee_amount
              ? parseFloat(student.fee_amount)
              : null,
            fee_plan_name: student.fee_plan_name || null,
            scholarship_id: student.scholarship_option
              ? null
              : student.scholarship_id
                ? parseInt(student.scholarship_id, 10)
                : null,
            scholarship_option: student.scholarship_option || null,
            custom_scholarship_amount: student.custom_scholarship_amount
              ? parseFloat(student.custom_scholarship_amount)
              : null,
            fee_status: student.fee_status || "Unpaid",
            guardian_phone: student.guardian_phone || null,
            guardian_email: student.guardian_email || null,
            guardian_address: student.guardian_address || null,
            guardian_cnic: student.guardian_cnic || null,
            emergency_contact_name: student.emergency_contact_name || null,
            emergency_contact_number: student.emergency_contact_number || null,
            photo_data: student.photo_data || null,
          }),
        }
      );

      if (res.status === 401) {
        onLogout && onLogout();
        return false;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Failed to update student");
      }

      showToast && showToast(
        "success",
        "Student Updated",
        `${student.name} record has been updated.`
      );
      return true;
    } catch (err) {
      console.error(err);
      showToast && showToast(
        "error",
        "Update Failed",
        err.message || "Could not update the student."
      );
      return false;
    }
  };

  // Withdraw student
  const handleWithdrawStudent = async (student, withdrawalData) => {
    const token = localStorage.getItem("als_token");
    const apiStudentId = student?.student_id || student?.id;
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/students/${apiStudentId}/withdraw`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            withdrawal_date: withdrawalData.withdrawal_date,
            reason: withdrawalData.reason,
          }),
        }
      );

      if (res.status === 401) {
        onLogout && onLogout();
        return null;
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Failed to withdraw student");
      }
      showToast && showToast(
        "success",
        "Student Withdrawn",
        `${student.name} has been successfully withdrawn.`
      );
      return { ...data, id: data.id, student_id: data.student_id };
    } catch (err) {
      console.error(err);
      showToast && showToast(
        "error",
        "Withdrawal Failed",
        err.message || "Could not withdraw student."
      );
      return null;
    }
  };

  const promoteOneStudent = async (student, payload) => {
    const token = localStorage.getItem("als_token");
    const apiStudentId = student?.student_id || student?.id;
    const res = await fetch(`${API_BASE_URL}/api/students/${apiStudentId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });
    if (res.status === 401) {
      onLogout && onLogout();
      throw new Error("__unauthorized__");
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.detail || "Failed to promote student");
    return { ...data, id: data.id, student_id: data.student_id };
  };

  // Promote student
  const handlePromoteStudent = async (student, payload) => {
    try {
      const updated = await promoteOneStudent(student, payload);
      showToast && showToast(
        "success",
        "Student Promoted",
        `${updated.name} promoted to ${updated.class_name || updated.student_status}.`
      );
      return updated;
    } catch (err) {
      if (err.message !== "__unauthorized__") {
        console.error(err);
        showToast && showToast(
          "error",
          "Promotion Failed",
          err.message || `Could not promote ${student.name}.`
        );
      }
      return null;
    }
  };

  // Bulk promote class students
  const handlePromoteFiltered = async (studentRecords, payload) => {
    const results = [];
    let failedCount = 0;
    for (const student of studentRecords) {
      try {
        const updated = await promoteOneStudent(student, payload);
        results.push({ before: student, after: updated });
      } catch (err) {
        if (err.message === "__unauthorized__") return results;
        failedCount += 1;
      }
    }
    if (failedCount > 0) {
      showToast && showToast(
        "error",
        "Promotion Partial",
        `${results.length} promoted, ${failedCount} failed.`
      );
    } else {
      showToast && showToast(
        "success",
        "Promotion Successful",
        `All ${results.length} student(s) promoted.`
      );
    }
    return results;
  };

  // Bulk delete class students
  const handleDeleteFilteredStudents = async (studentRecords, password) => {
    const token = localStorage.getItem("als_token");
    const studentIds = studentRecords
      .map((student) => student?.student_id || student?.id)
      .filter(Boolean);

    if (studentIds.length === 0) {
      showToast && showToast(
        "error",
        "Bulk Deletion Failed",
        "No valid student IDs were selected."
      );
      throw new Error("No valid student IDs were selected.");
    }

    try {
      const res = await fetch(`${API_BASE_URL}/api/students/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          student_ids: studentIds,
          password,
        }),
      });

      if (res.status === 401) {
        const errData = await res.json().catch(() => ({}));
        if (errData.detail === "Invalid password") {
          showToast && showToast(
            "error",
            "Authentication Failed",
            "The password you entered is incorrect."
          );
          throw new Error("Invalid password");
        }
        onLogout && onLogout();
        throw new Error("Session expired");
      }

      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(
          errData.detail || "Could not delete selected students."
        );
      }

      const result = await res.json();
      const missingIds = result.missing_ids || [];
      const deletedStudentIds = new Set(
        studentIds.filter((id) => !missingIds.includes(id))
      );
      const deletedStudents = studentRecords.filter((s) =>
        deletedStudentIds.has(s.student_id || s.id)
      );

      showToast && showToast(
        "success",
        "Students Deleted",
        `Successfully deleted ${deletedStudents.length} student records.`
      );
      return deletedStudents;
    } catch (err) {
      console.error(err);
      if (err.message !== "Invalid password" && err.message !== "Session expired") {
        showToast && showToast(
          "error",
          "Bulk Deletion Failed",
          err.message || "Could not complete bulk deletion."
        );
      }
      throw err;
    }
  };

  return (
    <StudentListBase
      mode="manage"
      lockedCampusId={campusId}
      lockedClassId={classId}
      lockedClassName={className}
      userRole="superadmin"
      onLogout={onLogout}
      showToast={showToast}
      onSaveEdit={handleSaveEdit}
      onWithdrawStudent={handleWithdrawStudent}
      onPromoteStudent={handlePromoteStudent}
      onPromoteFiltered={handlePromoteFiltered}
      onDeleteFiltered={handleDeleteFilteredStudents}
      onBack={onBack}
      canEdit={true}
      canDelete={true}
      canWithdraw={true}
    />
  );
}

export default StudentList;
